// src/components/NotificationConfig/forms/PushConfigForm.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faFire,
    faCheck,
    faTimes,
    faEye,
    faEyeSlash,
    faExternalLinkAlt,
    faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useNotificationContext } from '../../../contexts/NotificationContext';

const PushConfigForm = ({ marcaId }) => {
    const { getChannelConfig, updateConfig, loading, requestPermission } = useNotificationContext();
    const [config, setConfig] = useState({
        enabled: false,
        firebaseConfig: {
            apiKey: '',
            authDomain: '',
            projectId: '',
            storageBucket: '',
            messagingSenderId: '',
            appId: ''
        },
        vapidKey: '',
        serverKey: ''
    });
    const [showKeys, setShowKeys] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [isTesting, setIsTesting] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('default');

    useEffect(() => {
        if (marcaId) {
            loadConfig();
        }
        checkPermissionStatus();
    }, [marcaId]);

    const loadConfig = () => {
        const pushConfig = getChannelConfig('push');
        setConfig({
            enabled: pushConfig.enabled || false,
            firebaseConfig: pushConfig.firebaseConfig || {
                apiKey: '',
                authDomain: '',
                projectId: '',
                storageBucket: '',
                messagingSenderId: '',
                appId: ''
            },
            vapidKey: pushConfig.vapidKey || '',
            serverKey: pushConfig.serverKey || ''
        });
    };

    const checkPermissionStatus = () => {
        if ('Notification' in window) {
            setPermissionStatus(Notification.permission);
        }
    };

    const handleChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setConfig(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setConfig(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await updateConfig(marcaId, {
                push: config
            });

            setTestResult({ success: true, message: 'Configuración guardada correctamente' });
            setTimeout(() => setTestResult(null), 3000);
        } catch (error) {
            setTestResult({ success: false, message: error.message });
        }
    };

    const handleRequestPermission = async () => {
        try {
            const granted = await requestPermission();
            setPermissionStatus(granted ? 'granted' : 'denied');

            if (granted) {
                setTestResult({ success: true, message: 'Permisos de notificación concedidos' });
            } else {
                setTestResult({ success: false, message: 'Permisos de notificación denegados' });
            }

            setTimeout(() => setTestResult(null), 3000);
        } catch (error) {
            setTestResult({ success: false, message: error.message });
        }
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult(null);

        try {
            const response = await fetch('/api/notifications/test-push', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth-storage-petitprime')}`
                },
                body: JSON.stringify({
                    marcaId,
                    config
                })
            });

            const result = await response.json();

            if (response.ok) {
                setTestResult({ success: true, message: 'Notificación push enviada correctamente' });
            } else {
                setTestResult({ success: false, message: result.message || 'Error enviando notificación push' });
            }
        } catch (error) {
            setTestResult({ success: false, message: 'Error de conexión' });
        } finally {
            setIsTesting(false);
        }
    };

    const openFirebaseConsole = () => {
        window.open('https://console.firebase.google.com/', '_blank');
    };

    const downloadServiceWorker = () => {
        const serviceWorkerContent = `// firebase-messaging-sw.js
importScripts('https://www.gstatic.com/firebasejs/9.0.0/firebase-app-compat.js');
importScripts('https://www.gstatic.com/firebasejs/9.0.0/firebase-messaging-compat.js');

const firebaseConfig = ${JSON.stringify(config.firebaseConfig, null, 2)};

firebase.initializeApp(firebaseConfig);
const messaging = firebase.messaging();

messaging.onBackgroundMessage((payload) => {
  const notificationTitle = payload.notification.title;
  const notificationOptions = {
    body: payload.notification.body,
    icon: payload.notification.icon || '/logo.png',
    badge: '/logo.png',
    tag: payload.data?.tag || 'default',
    data: payload.data
  };

  return self.registration.showNotification(notificationTitle, notificationOptions);
});`;

        const blob = new Blob([serviceWorkerContent], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'firebase-messaging-sw.js';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const getPermissionStatusColor = () => {
        switch (permissionStatus) {
            case 'granted': return 'text-green-600';
            case 'denied': return 'text-red-600';
            default: return 'text-yellow-600';
        }
    };

    const getPermissionStatusText = () => {
        switch (permissionStatus) {
            case 'granted': return 'Concedidos';
            case 'denied': return 'Denegados';
            default: return 'No solicitados';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
                <FontAwesomeIcon icon={faBell} className="text-blue-500 text-xl mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">Configuración de Push Notifications</h3>
                <div className="ml-auto">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => handleChange('enabled', e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                            {config.enabled ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                    </label>
                </div>
            </div>

            {/* Estado de permisos */}
            <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-medium text-gray-700">Estado de Permisos</h4>
                        <p className={`text-sm ${getPermissionStatusColor()}`}>
                            {getPermissionStatusText()}
                        </p>
                    </div>
                    {permissionStatus !== 'granted' && (
                        <button
                            type="button"
                            onClick={handleRequestPermission}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600"
                        >
                            Solicitar Permisos
                        </button>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Información de ayuda */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                        <FontAwesomeIcon icon={faFire} className="mr-2" />
                        Configuración de Firebase
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                        Para configurar las notificaciones push, necesitas crear un proyecto en Firebase y obtener las credenciales.
                    </p>
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={openFirebaseConsole}
                            className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                            Firebase Console
                        </button>
                        <button
                            type="button"
                            onClick={downloadServiceWorker}
                            className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center"
                        >
                            <FontAwesomeIcon icon={faDownload} className="mr-1" />
                            Descargar Service Worker
                        </button>
                    </div>
                </div>

                {/* Configuración de Firebase */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            API Key
                        </label>
                        <div className="relative">
                            <input
                                type={showKeys ? 'text' : 'password'}
                                value={config.firebaseConfig.apiKey}
                                onChange={(e) => handleChange('firebaseConfig.apiKey', e.target.value)}
                                placeholder="AIzaSy..."
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={!config.enabled}
                            />
                            <button
                                type="button"
                                onClick={() => setShowKeys(!showKeys)}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                disabled={!config.enabled}
                            >
                                <FontAwesomeIcon icon={showKeys ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Auth Domain
                        </label>
                        <input
                            type="text"
                            value={config.firebaseConfig.authDomain}
                            onChange={(e) => handleChange('firebaseConfig.authDomain', e.target.value)}
                            placeholder="tu-proyecto.firebaseapp.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Project ID
                        </label>
                        <input
                            type="text"
                            value={config.firebaseConfig.projectId}
                            onChange={(e) => handleChange('firebaseConfig.projectId', e.target.value)}
                            placeholder="tu-proyecto-id"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Storage Bucket
                        </label>
                        <input
                            type="text"
                            value={config.firebaseConfig.storageBucket}
                            onChange={(e) => handleChange('firebaseConfig.storageBucket', e.target.value)}
                            placeholder="tu-proyecto.appspot.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Messaging Sender ID
                        </label>
                        <input
                            type="text"
                            value={config.firebaseConfig.messagingSenderId}
                            onChange={(e) => handleChange('firebaseConfig.messagingSenderId', e.target.value)}
                            placeholder="123456789"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            App ID
                        </label>
                        <input
                            type="text"
                            value={config.firebaseConfig.appId}
                            onChange={(e) => handleChange('firebaseConfig.appId', e.target.value)}
                            placeholder="1:123456789:web:abcdef"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                    </div>
                </div>

                {/* VAPID Key */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        VAPID Key
                    </label>
                    <div className="relative">
                        <input
                            type={showKeys ? 'text' : 'password'}
                            value={config.vapidKey}
                            onChange={(e) => handleChange('vapidKey', e.target.value)}
                            placeholder="BL..."
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                        <button
                            type="button"
                            onClick={() => setShowKeys(!showKeys)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            disabled={!config.enabled}
                        >
                            <FontAwesomeIcon icon={showKeys ? faEyeSlash : faEye} />
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Clave VAPID para autenticación de push notifications
                    </p>
                </div>

                {/* Server Key */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Server Key (Opcional)
                    </label>
                    <div className="relative">
                        <input
                            type={showKeys ? 'text' : 'password'}
                            value={config.serverKey}
                            onChange={(e) => handleChange('serverKey', e.target.value)}
                            placeholder="AAAA..."
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!config.enabled}
                        />
                        <button
                            type="button"
                            onClick={() => setShowKeys(!showKeys)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            disabled={!config.enabled}
                        >
                            <FontAwesomeIcon icon={showKeys ? faEyeSlash : faEye} />
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Clave del servidor para envío desde backend
                    </p>
                </div>

                {/* Mensaje de resultado */}
                {testResult && (
                    <div className={`p-3 rounded-md ${testResult.success
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        <div className="flex items-center">
                            <FontAwesomeIcon
                                icon={testResult.success ? faCheck : faTimes}
                                className={`mr-2 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}
                            />
                            {testResult.message}
                        </div>
                    </div>
                )}

                {/* Botones */}
                <div className="flex space-x-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading || !config.enabled}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Guardando...' : 'Guardar Configuración'}
                    </button>

                    <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={isTesting || !config.enabled || permissionStatus !== 'granted'}
                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isTesting ? 'Enviando...' : 'Enviar Notificación de Prueba'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PushConfigForm; 